import React, { useState, useEffect, useCallback } from 'react';
import {
    getUniformDataAuditLogs,
    getActionTypes,
    getAuditLogSchools,
    getAuditLogActivities,
    getStudentUniformDetails
} from '../../services/api';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import './UniformDataAuditLog.css';

const UniformDataAuditLog = () => {
    const { t } = useTranslation(['uniformDataAuditLog', 'common']);
    const [logs, setLogs] = useState([]);
    const [actionTypes, setActionTypes] = useState([]);
    const [schools, setSchools] = useState([]);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Expandable rows
    const [expandedRows, setExpandedRows] = useState({});
    const [studentDetails, setStudentDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});

    // Filter states
    const [dateFrom, setDateFrom] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 7); // Default: Last 7 days
        return date.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [selectedSchool, setSelectedSchool] = useState('');
    const [selectedActivity, setSelectedActivity] = useState('');
    const [selectedActionType, setSelectedActionType] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination states
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Report Issue modal states
    const [showReportIssueModal, setShowReportIssueModal] = useState(false);
    const [screenshot, setScreenshot] = useState(null);

    // Load dropdown data on mount
    useEffect(() => {
        loadActionTypes();
        loadSchools();
        loadActivities();
    }, []);

    // Load logs when filters change
    useEffect(() => {
        loadLogs();
    }, [page, pageSize, dateFrom, dateTo, selectedSchool, selectedActivity, selectedActionType, searchTerm]);

    const loadActionTypes = async () => {
        try {
            const response = await getActionTypes('UniformData');
            if (response.success && response.data) {
                setActionTypes(response.data);
            }
        } catch (err) {
            console.error('Error loading action types:', err);
        }
    };

    const loadSchools = async () => {
        try {
            const response = await getAuditLogSchools('UniformDataAuditLog');
            if (response.success && response.data) {
                setSchools(response.data);
            }
        } catch (err) {
            console.error('Error loading schools:', err);
        }
    };

    const loadActivities = async () => {
        try {
            const response = await getAuditLogActivities();
            if (response.success && response.data) {
                setActivities(response.data);
            }
        } catch (err) {
            console.error('Error loading activities:', err);
        }
    };

    const loadLogs = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const filters = {
                fromDate: dateFrom,
                toDate: dateTo,
                schoolCode: selectedSchool || undefined,
                activityId: selectedActivity ? parseInt(selectedActivity) : undefined,
                actionType: selectedActionType || undefined,
                performedBy: searchTerm || undefined,
                pageNumber: page,
                pageSize
            };

            const response = await getUniformDataAuditLogs(filters);

            if (response.success && response.data) {
                setLogs(response.data.data || []);
                setTotalCount(response.data.totalCount || 0);
                setTotalPages(response.data.totalPages || 0);
            } else {
                throw new Error(response.message || t('uniformDataAuditLog:messages.error.loadFailed'));
            }
        } catch (err) {
            console.error('Error loading audit logs:', err);
            setError(err.message || t('uniformDataAuditLog:messages.error.loadFailed'));
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, selectedSchool, selectedActivity, selectedActionType, searchTerm, page, pageSize]);

    const toggleRowExpansion = async (logId, log) => {
        const isExpanded = expandedRows[logId];

        if (isExpanded) {
            // Collapse
            setExpandedRows(prev => ({ ...prev, [logId]: false }));
        } else {
            // Expand and load details if not already loaded
            setExpandedRows(prev => ({ ...prev, [logId]: true }));

            if (!studentDetails[logId] && log.schoolCode && log.year && log.grade && log.activityId) {
                setLoadingDetails(prev => ({ ...prev, [logId]: true }));

                try {
                    const response = await getStudentUniformDetails(
                        log.schoolCode,
                        log.year,
                        log.grade,
                        log.activityId
                    );

                    if (response.success && response.data) {
                        setStudentDetails(prev => ({ ...prev, [logId]: response.data }));
                    }
                } catch (err) {
                    console.error('Error loading student details:', err);
                } finally {
                    setLoadingDetails(prev => ({ ...prev, [logId]: false }));
                }
            }
        }
    };

    const handleSearch = () => {
        setPage(1); // Reset to first page
        loadLogs();
    };

    const handleReset = () => {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        setDateFrom(date.toISOString().split('T')[0]);
        setDateTo(new Date().toISOString().split('T')[0]);
        setSelectedSchool('');
        setSelectedActivity('');
        setSelectedActionType('');
        setSearchTerm('');
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-MY', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    };

    // Handle Report Issue button click
    const handleReportIssueClick = async () => {
        try {
            Swal.fire({
                title: t('uniformDataAuditLog:messages.error.capturingScreenshot'),
                html: t('uniformDataAuditLog:messages.error.pleaseWait'),
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const canvas = await html2canvas(document.body, {
                useCORS: true,
                allowTaint: true,
                scrollY: -window.scrollY,
                scrollX: -window.scrollX,
                width: window.innerWidth,
                height: window.innerHeight
            });

            const imageData = canvas.toDataURL('image/png');
            setScreenshot(imageData);
            setShowReportIssueModal(true);
            Swal.close();
        } catch (error) {
            console.error('Error capturing screenshot:', error);
            Swal.fire({
                icon: 'error',
                title: t('uniformDataAuditLog:messages.error.captureFailed'),
                text: t('uniformDataAuditLog:messages.error.captureFailedMessage')
            });
        }
    };

    return (
        <div className="uniform-data-audit-log-container">
            {/* Header with Logo and Title */}
            <div className="header-container">
                <img className="logo" src="/KTC.png" alt="KTC" />
                <h2>{t('uniformDataAuditLog:title')} ({t('uniformDataAuditLog:titleEn')})</h2>
                <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('uniformDataAuditLog:tooltips.reportIssue')}>
                    <i className="fas fa-bug"></i>
                </button>
            </div>

            {/* Filter Panel */}
            <div className="filter-panel">
                <div className="filter-row">
                    <div className="filter-group">
                        <label>{t('uniformDataAuditLog:filters.dateFrom')}</label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="form-control"
                        />
                    </div>

                    <div className="filter-group">
                        <label>{t('uniformDataAuditLog:filters.dateTo')}</label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="form-control"
                        />
                    </div>

                    <div className="filter-group">
                        <label>{t('uniformDataAuditLog:filters.school')}</label>
                        <select
                            value={selectedSchool}
                            onChange={(e) => setSelectedSchool(e.target.value)}
                            className="form-control"
                        >
                            <option value="">{t('uniformDataAuditLog:filters.allSchools')}</option>
                            {schools.map(school => (
                                <option key={school.schoolCode} value={school.schoolCode}>
                                    {school.schoolCode} - {school.schoolName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>{t('uniformDataAuditLog:filters.activity')}</label>
                        <select
                            value={selectedActivity}
                            onChange={(e) => setSelectedActivity(e.target.value)}
                            className="form-control"
                        >
                            <option value="">{t('uniformDataAuditLog:filters.allActivities')}</option>
                            {activities.map(activity => (
                                <option key={activity.activityId} value={activity.activityId}>
                                    {activity.activityName} ({activity.enrolYear})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="filter-row">
                    <div className="filter-group">
                        <label>{t('uniformDataAuditLog:filters.actionType')}</label>
                        <select
                            value={selectedActionType}
                            onChange={(e) => setSelectedActionType(e.target.value)}
                            className="form-control"
                        >
                            <option value="">{t('uniformDataAuditLog:filters.allActions')}</option>
                            {actionTypes.map(at => (
                                <option key={at.value} value={at.value}>
                                    {at.displayText}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>{t('uniformDataAuditLog:filters.search')}</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={t('uniformDataAuditLog:filters.searchPlaceholder')}
                            className="form-control"
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>

                    <div className="filter-actions">
                        <button onClick={handleSearch} className="btn btn-primary">
                            {t('uniformDataAuditLog:buttons.search')}
                        </button>
                        <button onClick={handleReset} className="btn btn-secondary">
                            {t('uniformDataAuditLog:buttons.reset')}
                        </button>
                    </div>

                    <div className="filter-group">
                        <label>{t('uniformDataAuditLog:filters.pageSize')}</label>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                            className="form-control"
                        >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>

                    <div className="result-summary">
                        {t('uniformDataAuditLog:pagination.showing')} {logs.length > 0 ? ((page - 1) * pageSize + 1) : 0} {t('uniformDataAuditLog:pagination.to')} {Math.min(page * pageSize, totalCount)} {t('uniformDataAuditLog:pagination.of')} {totalCount} {t('uniformDataAuditLog:pagination.records')}
                    </div>
                </div>
            </div>

            {/* Results Table */}
            {loading ? (
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>{t('uniformDataAuditLog:loading')}</p>
                </div>
            ) : error ? (
                <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    <p>{error}</p>
                    <button onClick={loadLogs} className="btn btn-primary">{t('uniformDataAuditLog:buttons.retry')}</button>
                </div>
            ) : (
                <>
                    <div className="table-container">
                        <table className="audit-log-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th>{t('uniformDataAuditLog:table.headers.dateTime')}</th>
                                    <th>{t('uniformDataAuditLog:table.headers.action')}</th>
                                    <th>{t('uniformDataAuditLog:table.headers.school')}</th>
                                    <th>{t('uniformDataAuditLog:table.headers.summary')}</th>
                                    <th>{t('uniformDataAuditLog:table.headers.performedBy')}</th>
                                    <th>{t('uniformDataAuditLog:table.headers.role')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="no-data">
                                            {t('uniformDataAuditLog:table.noData')}
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <React.Fragment key={log.id}>
                                            <tr className="main-row">
                                                <td className="expand-cell">
                                                    <button
                                                        onClick={() => toggleRowExpansion(log.id, log)}
                                                        className="expand-button"
                                                        title={expandedRows[log.id] ? t('uniformDataAuditLog:buttons.collapse') : t('uniformDataAuditLog:buttons.expand')}
                                                    >
                                                        <i className={`fas fa-chevron-${expandedRows[log.id] ? 'down' : 'right'}`}></i>
                                                    </button>
                                                </td>
                                                <td className="datetime-column">
                                                    {formatDateTime(log.timestamp)}
                                                </td>
                                                <td>
                                                    <span className={`action-badge ${log.actionTypeBadgeClass}`}>
                                                        {log.actionTypeDisplay}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="school-info">
                                                        <div className="school-code">{log.schoolNameShort || log.schoolCode}</div>
                                                        <div className="school-name">{log.schoolName}</div>
                                                    </div>
                                                </td>
                                                <td className="summary-column">
                                                    {log.summary}
                                                </td>
                                                <td>{log.performedBy}</td>
                                                <td>{log.performedByRole}</td>
                                            </tr>

                                            {/* Expanded Details Row */}
                                            {expandedRows[log.id] && (
                                                <tr className="details-row">
                                                    <td colSpan="7">
                                                        <div className="details-panel">
                                                            <h4>{t('uniformDataAuditLog:detailsPanel.title')}</h4>
                                                            <p className="details-info">
                                                                <strong>{t('uniformDataAuditLog:detailsPanel.activity')}</strong> {log.activityName} ({log.year}) |
                                                                <strong> {t('uniformDataAuditLog:detailsPanel.grade')}</strong> {log.grade}
                                                            </p>

                                                            {loadingDetails[log.id] ? (
                                                                <div className="details-loading">
                                                                    <div className="spinner-small"></div>
                                                                    <span>{t('uniformDataAuditLog:detailsPanel.loadingDetails')}</span>
                                                                </div>
                                                            ) : studentDetails[log.id] && studentDetails[log.id].length > 0 ? (
                                                                <div className="student-details-table-container">
                                                                    <table className="student-details-table">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>{t('uniformDataAuditLog:table.headers.no')}</th>
                                                                                <th>{t('uniformDataAuditLog:table.headers.studentName')}</th>
                                                                                <th>{t('uniformDataAuditLog:table.headers.ic')}</th>
                                                                                <th>{t('uniformDataAuditLog:table.headers.uniformSizes')}</th>
                                                                                <th>{t('uniformDataAuditLog:table.headers.lastModified')}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {studentDetails[log.id].map((student, idx) => (
                                                                                <tr key={idx}>
                                                                                    <td>{idx + 1}</td>
                                                                                    <td>{student.studentName}</td>
                                                                                    <td>{student.studentIC}</td>
                                                                                    <td className="uniform-sizes">
                                                                                        {student.uniformSizesSummary || '-'}
                                                                                    </td>
                                                                                    <td className="modified-info">
                                                                                        <div>{student.lastModifiedBy}</div>
                                                                                        {student.lastModifiedAt && (
                                                                                            <div className="modified-date">
                                                                                                {formatDateTime(student.lastModifiedAt)}
                                                                                            </div>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            ) : (
                                                                <div className="no-students">
                                                                    {t('uniformDataAuditLog:table.noStudents')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="btn btn-secondary"
                            >
                                {t('uniformDataAuditLog:buttons.previous')}
                            </button>

                            <div className="page-numbers">
                                {[...Array(Math.min(10, totalPages))].map((_, idx) => {
                                    let pageNum;
                                    if (totalPages <= 10) {
                                        pageNum = idx + 1;
                                    } else if (page <= 5) {
                                        pageNum = idx + 1;
                                    } else if (page >= totalPages - 4) {
                                        pageNum = totalPages - 9 + idx;
                                    } else {
                                        pageNum = page - 4 + idx;
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`btn ${page === pageNum ? 'btn-primary' : 'btn-secondary'}`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages}
                                className="btn btn-secondary"
                            >
                                {t('uniformDataAuditLog:buttons.next')}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Report Issue Modal */}
            {showReportIssueModal && (
                <ReportIssueModal
                    show={showReportIssueModal}
                    onClose={() => setShowReportIssueModal(false)}
                    screenshot={screenshot}
                    context={{
                        screen: t('uniformDataAuditLog:context.screen'),
                        dateRange: { from: dateFrom, to: dateTo },
                        selectedSchool: selectedSchool,
                        selectedActivity: selectedActivity,
                        selectedActionType: selectedActionType,
                        searchTerm: searchTerm,
                        page: page,
                        pageSize: pageSize,
                        totalCount: totalCount
                    }}
                />
            )}
        </div>
    );
};

export default UniformDataAuditLog;
